import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDMY, todayNZ } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { WeeklyTimesheetDialog } from "@/components/WeeklyTimesheetDialog";
import { FileText, LogOut, PenLine, ShieldAlert } from "lucide-react";

export default function LimitedCleanerApp() {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const [tsDate, setTsDate] = useState(null);
  const periods = user?.open_periods || [];

  const { data: unsigned } = useQuery({
    queryKey: ["limited-unsigned"],
    queryFn: async () => (await api.get("/my/timesheet/unsigned")).data,
  });
  const unsignedWeeks = unsigned?.weeks || [];
  const isUnsigned = (p) => unsignedWeeks.some((w) => w.week_start >= p.period_start && w.week_start <= p.period_end);

  return (
    <div className="mx-auto min-h-screen max-w-lg space-y-4 p-4" data-testid="limited-cleaner-app">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs text-muted-foreground">{user?.name}</div>
          <h1 className="text-xl font-semibold">{t("Your timesheets")}</h1>
        </div>
        <Button variant="outline" size="sm" onClick={logout} data-testid="limited-logout-btn" className="gap-2">
          <LogOut className="h-4 w-4" /> {t("Sign out")}
        </Button>
      </div>

      <Card className="flex items-start gap-3 border-amber-300 bg-amber-50 p-3 text-sm" data-testid="limited-access-banner">
        <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
        <p className="text-amber-900">
          {t("Your account is disabled. You can still check and sign your timesheets until your manager closes off the pay period.")}
        </p>
      </Card>

      {periods.length === 0 ? (
        <p className="py-8 text-center text-sm text-muted-foreground">{t("No open timesheets.")}</p>
      ) : (
        <div className="space-y-2">
          {periods.map((p) => (
            <Card key={p.period_start} className="flex items-center justify-between gap-3 p-3" data-testid={`limited-period-${p.period_start}`}>
              <div>
                <div className="text-sm font-medium">{fmtDMY(p.period_start)} – {fmtDMY(p.period_end)}</div>
                <div className={`text-xs ${isUnsigned(p) ? "text-amber-700" : "text-muted-foreground"}`}>
                  {isUnsigned(p) ? t("Signature needed") : t("Open — awaiting manager close-off")}
                </div>
              </div>
              <Button size="sm" onClick={() => setTsDate(p.period_end)} data-testid={`limited-open-ts-${p.period_start}`} className="gap-2">
                {isUnsigned(p) ? <PenLine className="h-4 w-4" /> : <FileText className="h-4 w-4" />} {t("View")}
              </Button>
            </Card>
          ))}
        </div>
      )}

      <WeeklyTimesheetDialog open={!!tsDate} onOpenChange={(o) => !o && setTsDate(null)} mode="cleaner" date={tsDate || todayNZ()} />
    </div>
  );
}
